import { getUserLocale } from "@/i18n/locales";
import {
  getCategories,
  getProductsByCategoryId,
} from "@frontend/lib/actions/actions";
import { getOrgDataFromOrgTitle } from "@frontend/lib/actions/market-graphql-queries";
import { CategorySection } from "./CategorySection";
import { CategorySelector } from "./CategorySelector";

// TODO: Need to make this more dynamic, take into account styling from the backend
// and make it more responsive.
export default async function CategoryList({
  params,
  props,
}: {
  params: { clientSlug: string; orgId: number };
  props?: {
    scrollId: string;
    priceToggleColor: string;
    displayMode: "default" | "grid" | "accordion";
    showSelector?: boolean;
    selectorTitle?: string;
  };
}) {
  const organization = await getOrgDataFromOrgTitle(
    params.clientSlug.toUpperCase(),
  );
  const orgId = organization.findFirstOrganization?.id;

  // Org does not exist under that name
  if (!orgId) {
    return <div>Org not found</div>;
  }

  // Get language code for filtering translations on the frontend
  const languageCode = await getUserLocale();

  const categories = await getCategories(orgId).then((categories) =>
    categories
      .sort((a, b) => b.popularity - a.popularity)
      .filter((category) => category.name !== "Featured"),
  );

  const products = await getProductsByCategoryId(
    categories.map((category) => category.id),
  );

  if (!products) {
    return;
  }
  // Filter out products where the hrts are not active
  const productsWithActiveHrts = products.filter((product) => {
    return product.productToHrt.some((hrt) => hrt.hrt.isConsumerFacing);
  });

  const groupedProductsByCategory = productsWithActiveHrts.reduce(
    (acc, product) => {
      // Iterate through all category IDs for the product
      for (const categoryId of product.categoryIds) {
        if (!acc[categoryId]) {
          acc[categoryId] = [];
        }
        acc[categoryId].push(product);
      }
      return acc;
    },
    {} as Record<string, typeof productsWithActiveHrts>,
  );

  // filter out categories with no products
  const categoriesWithProducts = categories.filter(
    (category) => groupedProductsByCategory[category.id]?.length > 0,
  );

  return (
    <div className="flex flex-col gap-0" id={props?.scrollId}>
      {props?.showSelector && (
        <CategorySelector
          title={props?.selectorTitle}
          categoriesWithProducts={categoriesWithProducts}
        />
      )}

      {categoriesWithProducts.map((category, index) => {
        const categoryTranslation = category.ProductCategoryTranslation.find(
          (translation) => translation.languageCode === languageCode,
        ); // otherwise regular name
        const categoryName = categoryTranslation?.name || category.name;
        return (
          <CategorySection
            key={category.id}
            category={{ id: category.id.toString(), name: categoryName }}
            products={groupedProductsByCategory[category.id] || []}
            clientSlug={params.clientSlug}
            languageCode={languageCode}
            defaultChecked={index === 0}
            displayMode={props?.displayMode}
            priceToggleColor={props?.priceToggleColor}
            orgId={orgId}
          />
        );
      })}
    </div>
  );
}
